import { useState } from 'react';
import { Route, Routes, useParams } from 'react-router';
import { Trans, useLingui } from '@lingui/react/macro';

import Stack from '@/design_system/Stack';
import Tabs from '@/design_system/Tabs';
import { Tab, TabList, TabPanel } from '@/design_system/Tabs/Tabs';
import EditableTitle from '@/design_system/Typography/EditableTitle';
import Paragraph from '@/design_system/Typography/Paragraph';
import { useBrand, useUpdateBrand } from '@/models/brand';
import { ErrorState } from '@/routes/SettingsBeta/components/ErrorState';
import { SettingsBreadcrumbParent } from '@/routes/SettingsBeta/components/Layout/Layout';
import { Loading } from '@/routes/SettingsBeta/components/Loading';

import { OverviewTab } from './Tabs/OverviewTab/OverviewTab';
import { RelatedBrandsTab } from './Tabs/RelatedBrandsTab';

export const Brand = () => {
  const { t } = useLingui();
  const { id } = useParams();
  const { data: brand, isLoading: isBrandLoading, error } = useBrand(id);
  const { mutateAsync: updateBrand, isPending: isUpdateBrandPending } = useUpdateBrand(id);

  if (isBrandLoading) {
    return <Loading />;
  }

  if (!brand || error) {
    return (
      <ErrorState
        errorMessage={
          <Paragraph>
            <Trans id="settings.organization.data-catalogs.brands.detail.errors.not-found">
              This brand could not be found or you do not have access to it.
            </Trans>
          </Paragraph>
        }
      />
    );
  }

  return (
    <main>
      <Stack
        row
        className="settings-section__record-header"
        gap="0.5rem"
        alignItems="center"
        flexWrap="wrap"
      >
        <SettingsBreadcrumbParent
          to="/settings_beta/catalog/brands"
          label={<Trans id="settings.organization.data-catalogs.brands">Brands</Trans>}
        />

        <EditableTitle
          value={brand.name}
          onSave={(newName) => updateBrand({ name: newName })}
          titleProps={{ as: 'h1', size: '200', weight: 'medium' }}
          editAriaLabel={t({
            id: 'settings.organization.data-catalogs.brands.detail.editable-title.edit',
            message: 'Edit brand name',
          })}
          saveAriaLabel={t({
            id: 'settings.organization.data-catalogs.brands.detail.editable-title.save',
            message: 'Save brand name',
          })}
          cancelAriaLabel={t({
            id: 'settings.organization.data-catalogs.brands.detail.editable-title.cancel',
            message: 'Cancel renaming',
          })}
          inputAriaLabel={t({
            id: 'settings.organization.data-catalogs.brands.detail.editable-title.input',
            message: 'Brand name',
          })}
          isSaving={isUpdateBrandPending}
        />
      </Stack>

      <BrandTabs brandId={brand.id} />
    </main>
  );
};

type Tab = 'overview' | 'related-brands';

const BrandTabs = ({ brandId }: { brandId: string }) => {
  const [tab, setTab] = useState<Tab>('overview');

  return (
    <Tabs selectedKey={tab}>
      <TabList>
        <Tab
          id="overview"
          onPress={() => setTab('overview')}
          href={`/settings_beta/catalog/brands/${brandId}`}
        >
          <Trans id="settings.organization.data-catalogs.brands.detail.tab.overview">
            Overview
          </Trans>
        </Tab>

        <Tab
          id="related-brands"
          onPress={() => setTab('related-brands')}
          href={`/settings_beta/catalog/brands/${brandId}/related-brands`}
        >
          <Trans id="settings.organization.data-catalogs.brands.detail.tab.related-brands">
            Related brands
          </Trans>
        </Tab>
      </TabList>

      <TabPanel id={tab}>
        <Routes>
          <Route path="" element={<OverviewTab />} />
          <Route path="related-brands" element={<RelatedBrandsTab />} />
        </Routes>
      </TabPanel>
    </Tabs>
  );
};
